import React, { useState } from 'react';
import { View, useWindowDimensions } from 'react-native';

import { useT } from '@transifex/react';
import axios from 'axios';

import { AuthFooter } from '../components/AuthFooter';
import { AuthLayout } from '../components/AuthLayout';
import { BloomButton } from '../components/BloomButton';
import { InlineError } from '../components/InlineError';
import { Logo } from '../components/Logo';
import { TextLink } from '../components/TextLink';
import ThemedText from '../components/ThemedText';
import { ROUTES, ERROR_MESSAGES } from '../utils/constants';
import { screenTestId } from '../utils/testId';
import { sendLoginLink } from '../api/magicLink';
import {
  clearPendingMagicLinkEmail,
  getPendingMagicLinkEmail,
} from '../services/magicLinkEmailMemo';
import { analytics } from '../utils/analytics';
import { useLoginRouter } from '../hooks/useLoginRouter';

const tid = screenTestId('MagicLinkSent');

/**
 * Post-send confirmation for magic-link login. Shows the email the link was
 * sent to (or falls back to a generic "your email" copy) and offers a
 * resend button.
 *
 * The email is intentionally NOT passed via a URL query param — an email
 * address in the URL would leak into browser history, server access logs,
 * Referer headers, screen shares, and analytics pageview auto-capture.
 * Instead we read a short-lived in-memory memo populated by /magic-link on
 * successful submit. If the memo is empty (hard refresh, direct navigation)
 * the screen renders the generic copy and any "Send again" tap bounces the
 * user back to /magic-link to re-enter their email.
 */
export default function MagicLinkSentScreen() {
  const router = useLoginRouter();
  const { width } = useWindowDimensions();
  const t = useT();

  const email = getPendingMagicLinkEmail() ?? '';

  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isDesktop = width >= 768;

  const handleResend = async () => {
    if (!email) {
      // In-memory memo was cleared (hard refresh, direct navigation) — bounce
      // back to the input screen so the user can re-enter their email.
      router.replace(ROUTES.MAGIC_LINK);
      return;
    }

    setError(null);
    setResent(false);
    setResending(true);
    analytics.trackEvent('magic_link_resend_clicked');

    try {
      await sendLoginLink({ email });
      analytics.trackEvent('magic_link_resend_succeeded');
      setResent(true);
    } catch (err) {
      const rateLimited =
        axios.isAxiosError(err) &&
        err.response?.status === 403 &&
        err.response?.data?.reason === 'rate_limited';
      analytics.trackEvent('magic_link_resend_failed', { rate_limited: rateLimited });
      setError(
        rateLimited
          ? ERROR_MESSAGES.EMAIL_LOGIN_LINK_RATE_LIMITED
          : ERROR_MESSAGES.EMAIL_LOGIN_LINK_FAILED,
      );
    } finally {
      setResending(false);
    }
  };

  const handleUseDifferentEmail = () => {
    clearPendingMagicLinkEmail();
    router.replace(ROUTES.MAGIC_LINK);
  };

  const handleBackToLogin = () => {
    clearPendingMagicLinkEmail();
    router.replace(ROUTES.LOGIN);
  };

  return (
    <AuthLayout>
      <View className='w-full'>
        <Logo />

        <View className='w-full'>
          <ThemedText
            type='titleHero'
            className='mb-2 font-bold'
            style={{ fontSize: 32, lineHeight: 40 }}
          >
            {t('Check your email')}
          </ThemedText>
          <ThemedText
            type='bodyRegular'
            color='subtle'
            className={isDesktop ? 'mb-6' : 'mb-8'}
          >
            {email
              ? t("If an account exists for {email}, you'll receive a sign in link. Tap the link in that email to sign in — it expires in 24 hours.", { email })
              : t("If an account exists for your email, you'll receive a sign in link. Tap the link in that email to sign in — it expires in 24 hours.")}
          </ThemedText>

          <InlineError message={error} testID={tid('error')} />

          {resent && (
            <View className='bg-login-green-10 rounded-lg p-3 px-9 mt-2 mb-4' testID={tid('resent-message')}>
              <ThemedText type='bodySmall' className='text-login-green-90 text-center'>
                {ERROR_MESSAGES.EMAIL_LOGIN_LINK_SUCCESS}
              </ThemedText>
            </View>
          )}

          <View>
            <BloomButton
              text={t('Send again')}
              onPress={handleResend}
              loading={resending}
              testID={tid('resend-button')}
            />
          </View>

          <View className='mt-4 items-center'>
            <TextLink
              href={ROUTES.MAGIC_LINK}
              onPress={handleUseDifferentEmail}
              testID={tid('different-email-link')}
            >
              {t('Use a different email')}
            </TextLink>
          </View>

          <AuthFooter
            variant='secondary'
            onBackToLogin={handleBackToLogin}
            testID={tid('footer')}
          />
        </View>
      </View>
    </AuthLayout>
  );
}
